export const DIALOG_DRAG_HANDLE_SELECTOR =
  '.p-dialog-header, .dialog-header, [data-pc-section="header"]'

export function eventTargetElement(target: EventTarget | null): Element | null {
  if (target == null) return null
  if (typeof Element !== 'undefined' && target instanceof Element) return target
  if (typeof Node !== 'undefined' && target instanceof Node) return target.parentElement
  return null
}

/** Visual px of the title bar that must stay inside the viewport. */
export const DIALOG_DRAG_HEADER_CLAMP_PX = 40

export interface CssPosition {
  left: number
  top: number
}

export interface VisualDelta {
  x: number
  y: number
}

export interface VisualSize {
  width: number
  height: number
}

export interface ViewportSize {
  width: number
  height: number
}

const normalizeZoom = (zoom: number): number =>
  Number.isFinite(zoom) && zoom > 0 ? zoom : 1

export function cssZoomOf(el: HTMLElement | null | undefined, fallback = 1): number {
  if (el) {
    const current = (el as HTMLElement & { currentCSSZoom?: number }).currentCSSZoom
    if (typeof current === 'number' && Number.isFinite(current) && current > 0) {
      return current
    }
  }
  return normalizeZoom(fallback)
}

export function visualToCss(visualPx: number, zoom: number): number {
  return visualPx / normalizeZoom(zoom)
}

/** Visual getBoundingClientRect → CSS left/top for `position:fixed` under CSS zoom. */
export function pinFixedFromRect(
  rect: { left: number; top: number },
  zoom: number,
): CssPosition {
  return {
    left: visualToCss(rect.left, zoom),
    top: visualToCss(rect.top, zoom),
  }
}

export function nextCssPosition(
  start: CssPosition,
  deltaVisual: VisualDelta,
  zoom: number,
): CssPosition {
  return {
    left: start.left + visualToCss(deltaVisual.x, zoom),
    top: start.top + visualToCss(deltaVisual.y, zoom),
  }
}

/** One-shot correction after pin if layout visual pos != target (subpixel / zoom rounding). */
export function correctPinnedFixed(
  current: CssPosition,
  targetVisual: { left: number; top: number },
  actualVisual: { left: number; top: number },
  zoom: number,
): CssPosition {
  return {
    left: current.left + visualToCss(targetVisual.left - actualVisual.left, zoom),
    top: current.top + visualToCss(targetVisual.top - actualVisual.top, zoom),
  }
}

/**
 * Keep ~40 visual px of the header in the viewport.
 * `size` and `viewport` are visual (getBoundingClientRect / innerWidth); `pos` is CSS px.
 */
export function clampDialogDragPosition(
  pos: CssPosition,
  sizeVisual: VisualSize,
  viewport: ViewportSize,
  zoom: number,
  headerClampVisualPx = DIALOG_DRAG_HEADER_CLAMP_PX,
): CssPosition {
  const z = normalizeZoom(zoom)
  const clampCss = visualToCss(headerClampVisualPx, z)
  const widthCss = visualToCss(sizeVisual.width, z)
  const viewportCss = {
    width: visualToCss(viewport.width, z),
    height: visualToCss(viewport.height, z),
  }

  const minLeft = clampCss - widthCss
  const maxLeft = viewportCss.width - clampCss
  const minTop = 0
  const maxTop = viewportCss.height - clampCss

  return {
    left: Math.min(maxLeft, Math.max(minLeft, pos.left)),
    top: Math.min(maxTop, Math.max(minTop, pos.top)),
  }
}

export function applyDialogFixedPosition(el: HTMLElement, pos: CssPosition): void {
  el.style.position = 'fixed'
  el.style.margin = '0'
  el.style.left = `${pos.left}px`
  el.style.top = `${pos.top}px`
}

export function cssPositionToStyle(pos: CssPosition | null): Record<string, string> {
  if (!pos) return {}
  return {
    position: 'fixed',
    margin: '0',
    left: `${pos.left}px`,
    top: `${pos.top}px`,
  }
}

export function setDialogDragWillChange(el: HTMLElement, active: boolean): void {
  el.style.willChange = active ? 'left, top' : ''
}
